import { createRootRoute, createRoute, createRouter, Outlet } from "@tanstack/react-router";
import { useConvexAuth, useQuery } from "convex/react";
import { api } from "../convex/_generated/api";
import { SignIn, SignUp, ResetPassword, Onboarding } from "./pages/Auth";
import { BillingGate, BillingPage, WelcomePage } from "./pages/Billing";
import { AppShell } from "./pages/AppShell";
import { Projects } from "./pages/Projects";
import { LibraryPage } from "./pages/Library";
import { Team } from "./pages/Team";
import { Platform } from "./pages/Platform";
import { ProjectLayout } from "./pages/project/Layout";
import { Overview } from "./pages/project/Overview";
import { SetupPage } from "./pages/project/Setup";
import { BreakdownsPage } from "./pages/project/Breakdowns";
import { CalendarPage } from "./pages/project/Calendar";
import { ProjectChains } from "./pages/project/Chains";
import { DeliverablesPage } from "./pages/project/Deliverables";
import { ProgressPage } from "./pages/project/Progress";
import { BudgetPage } from "./pages/project/Budget";
import { ActualsPage } from "./pages/project/Actuals";
import { PeriodsPage } from "./pages/project/Periods";
import { ReportsPage, type ReportKey } from "./pages/project/Reports";
import { AuditPage } from "./pages/project/Audit";
import { Spinner } from "./components/ui";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { useRouterState } from "@tanstack/react-router";

function ScreenBoundary({ children }: { children: React.ReactNode }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  return <ErrorBoundary resetKey={path}>{children}</ErrorBoundary>;
}

// Screens that work while the subscription isn't active (to fix it).
const BILLING_PATHS = ["/billing", "/welcome"];

function Root() {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const me = useQuery(api.account.me, isAuthenticated ? {} : "skip");
  if (isLoading || (isAuthenticated && me === undefined)) return <Spinner />;
  if (!isAuthenticated || !me) return path === "/signup" ? <SignUp /> : path === "/reset" ? <ResetPassword /> : <SignIn />;
  if (!me.role) return <Onboarding invited={!!me.pendingInvite} />;
  return (
    <AppShell me={me}>
      <ScreenBoundary>{(me.access?.ok ?? true) || BILLING_PATHS.includes(path) ? <Outlet /> : <BillingGate reason={me.access?.reason} />}</ScreenBoundary>
    </AppShell>
  );
}

const root = createRootRoute({ component: Root });
const projects = createRoute({ getParentRoute: () => root, path: "/", component: Projects });
const library = createRoute({
  getParentRoute: () => root,
  path: "/library",
  validateSearch: (s: Record<string, unknown>) => ({ tab: (s.tab as string) ?? "milestones" }),
  component: LibraryPage,
});
const team = createRoute({ getParentRoute: () => root, path: "/team", component: Team });
const platform = createRoute({ getParentRoute: () => root, path: "/platform", component: Platform });
const billing = createRoute({ getParentRoute: () => root, path: "/billing", component: BillingPage });
const welcome = createRoute({ getParentRoute: () => root, path: "/welcome", component: WelcomePage });
// Signed-in users who follow a sign-up or reset link land on their projects.
const signup = createRoute({ getParentRoute: () => root, path: "/signup", component: Projects });
const reset = createRoute({ getParentRoute: () => root, path: "/reset", component: Projects });
export const projectRoute = createRoute({ getParentRoute: () => root, path: "/p/$projectId", component: ProjectLayout });
const p = <P extends string>(path: P, component: () => React.ReactNode) => createRoute({ getParentRoute: () => projectRoute, path, component });
const reports = createRoute({
  getParentRoute: () => projectRoute,
  path: "reports/$report",
  component: () => {
    const { report } = reports.useParams();
    return <ReportsPage report={report as ReportKey} />;
  },
});

const tree = root.addChildren([
  projects,
  library,
  team,
  platform,
  billing,
  welcome,
  signup,
  reset,
  projectRoute.addChildren([
    p("/", Overview),
    p("setup", SetupPage),
    p("breakdowns", BreakdownsPage),
    p("calendar", CalendarPage),
    p("chains", ProjectChains),
    p("deliverables", DeliverablesPage),
    p("progress", ProgressPage),
    p("budget", BudgetPage),
    p("actuals", ActualsPage),
    p("periods", PeriodsPage),
    reports,
    p("audit", AuditPage),
  ]),
]);

export const router = createRouter({ routeTree: tree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
